// ============================================================================
// Línea de tiempo del ciclo de desarrollo (#559).
// ============================================================================
// El MISMO componente sirve a las dos audiencias, y esa es la garantía: no
// pinta nada que no venga en `steps`, y el backend ya decidió qué va en cada
// proyección (la del cliente no trae enlaces, ni autor, ni notas). Así no hay
// forma de que una vista de cliente filtre un dato técnico "por descuido".
// ============================================================================
import { formatDate, type DevStage, type StageStep } from "../services/serviceDeskService";

function StepDot({ step }: { step: StageStep }) {
  const done = step.status === "done";
  const current = step.status === "current";
  const color = done || current ? step.color : "#cbd5e1";
  return (
    <span
      aria-hidden
      className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border-2"
      style={{
        borderColor: color,
        backgroundColor: current ? color : done ? color : "transparent",
        boxShadow: current ? `0 0 0 3px ${color}33` : undefined,
      }}
    />
  );
}

export interface StageTimelineProps {
  steps: StageStep[];
  /** Etapa actual, para el texto accesible. */
  currentLabel?: string;
  compact?: boolean;
  isDarkMode?: boolean;
}

export function StageTimeline({ steps, currentLabel, compact = false, isDarkMode = false }: StageTimelineProps) {
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const text = isDarkMode ? "text-gray-100" : "text-gray-900";

  if (!steps || steps.length === 0) {
    return <p className={`text-xs ${muted}`}>Todavía no hay avances registrados.</p>;
  }

  return (
    <ol
      className="space-y-0"
      aria-label={currentLabel ? `Avance del ticket. Etapa actual: ${currentLabel}` : "Avance del ticket"}
    >
      {steps.map((step, i) => {
        const last = i === steps.length - 1;
        const reached = step.status !== "pending";
        return (
          <li key={step.id} className="flex gap-3">
            <div className="flex flex-col items-center">
              <StepDot step={step} />
              {!last && (
                <span
                  aria-hidden
                  className="w-0.5 flex-1"
                  style={{ minHeight: compact ? 14 : 20, backgroundColor: reached ? step.color : "#e2e8f0" }}
                />
              )}
            </div>
            <div className={`${last ? "" : "pb-2"} -mt-0.5`}>
              <p
                className={`text-sm ${step.status === "current" ? "font-semibold" : "font-medium"} ${
                  reached ? text : muted
                }`}
              >
                {step.label}
                {step.status === "current" && (
                  <span
                    className="ml-2 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                    style={{ color: step.color, backgroundColor: `${step.color}1a` }}
                  >
                    En curso
                  </span>
                )}
              </p>
              {/* Fecha: lo único, junto con la etapa, que el cliente ve. */}
              <p className={`text-[11px] ${muted}`}>{step.at ? formatDate(step.at) : "Pendiente"}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Tarjeta lista para la vista del CLIENTE (portal público y «mis tickets»).
 * Recibe el `devStage` tal cual: si el backend no mandó etapa, no pinta nada.
 */
export function CustomerStageCard({ devStage, className = "" }: { devStage?: DevStage | null; className?: string }) {
  if (!devStage || !devStage.timeline?.length) return null;
  return (
    <div className={`rounded-2xl border border-gray-200 bg-white p-6 shadow-sm ${className}`}>
      <h2 className="mb-1 text-sm font-semibold text-gray-700">Avance de tu solicitud</h2>
      <p className="mb-4 text-xs text-gray-500">
        Etapa actual: <strong>{devStage.label}</strong>
        {devStage.enteredAt ? ` · desde ${formatDate(devStage.enteredAt)}` : ""}
      </p>
      <StageTimeline steps={devStage.timeline} currentLabel={devStage.label} />
    </div>
  );
}

/** Chip compacto con la etapa actual, para listas y tarjetas. */
export function StageBadge({ devStage }: { devStage?: DevStage | null }) {
  if (!devStage?.current) return null;
  const color = devStage.timeline?.find((s) => s.id === devStage.current)?.color || "#64748b";
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
      style={{ color, backgroundColor: `${color}1a` }}
      title={`Etapa: ${devStage.label}`}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      {devStage.label}
    </span>
  );
}
